import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { initGTM, trackPageView, trackUserAction, trackCustomEvent, getVisitorId } from "../utils/gtm";
import useErrorTracker from "../hooks/useErrorTracker";

function buildUserInfo(userProp) {
  // Try to build user identity from prop or localStorage
  const info = {};
  try {
    if (userProp) {
      info.userId = userProp.id || userProp.user_id || userProp.userId;
      info.email = userProp.email;
      info.role = userProp.role || userProp.user_role;
    }
    if (!info.userId) {
      // Try 'auth' key first (newer auth system)
      const storedAuth = localStorage.getItem("auth");
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        info.userId = parsed.user_id || parsed.id;
        info.role = parsed.role;
        info.userName = parsed.user_name || parsed.name; 
        info.orgId = parsed.org_uid || parsed.org_id;
      }

      // Fallback to 'user' key (older auth system)
      if (!info.userId) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          info.userId = parsed.id || parsed.user_id || parsed.userId;
          info.email = parsed.email;
          info.role = parsed.role || parsed.user_role;
        }
      }
    }
  } catch (e) {
    console.warn("[GTM] Unable to parse user info:", e);
  }

  if (!info.userId) {
    info.userId = getVisitorId(); // fallback to anonymous visitor id
    info.loginState = "anonymous";
  } else {
    info.loginState = "logged_in";
  }

  return info;
}

export default function GTMTracker({ user }) {
  const location = useLocation();

  // Initialize global error tracker
  useErrorTracker();

  const userInfo = useMemo(() => buildUserInfo(user), [user]);

  useEffect(() => {
    // GA is initialized via script in index.html, just set user properties
    initGTM(null, userInfo);
  }, [userInfo]);

  useEffect(() => {
    trackPageView(location.pathname, userInfo);
  }, [location, userInfo]);

  useEffect(() => {
    const generateLabel = (el) => {
      if (!el) return null;
      const page = location.pathname || "unknown_page";
      const tag = el.tagName?.toLowerCase() || "unknown_tag";
      const id = el.id ? `#${el.id}` : "";
      const name = el.name ? `|name:${el.name}` : "";
      // Never the element's text, aria-label or value: on-screen text holds patient
      // names, and an input's value is whatever was typed (notes, phone numbers).
      // Use data-label for a readable event name.
      return `${page}|${tag}${id}${name}`;
    };

    const getActionLabel = (el) => {
      if (!el) return null;
      if (el.dataset?.label) return el.dataset.label;
      if (el.dataset?.gtmAction) return el.dataset.gtmAction;
      return generateLabel(el);
    };

    const toEventName = (str) => {
      return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    };

    const applyMissingLabels = () => {
      const candidates = document.querySelectorAll("button, a, [role='button'], input[type='button'], input[type='submit']");
      candidates.forEach((el) => {
        if (!el.dataset.gtmAction) {
          const lbl = generateLabel(el);
          if (lbl) el.dataset.gtmAction = lbl;
        }
      });
    };

    const handleClick = (event) => {
      const target = event.target;
      if (!target) return;

      const clickable = target.closest?.("button, a, [role='button'], input[type='button'], input[type='submit'], [data-gtm-action], [data-label]");
      if (!clickable) return;

      const label = getActionLabel(clickable);
      
      let payload = {
        label,
        tag: clickable.tagName?.toLowerCase(),
        id: clickable.id || undefined,
        class: clickable.className || undefined,
        path: location.pathname,
      };

      // Merge custom payload from data-gtm-payload attribute
      if (clickable.dataset?.gtmPayload) {
        try {
          const customPayload = JSON.parse(clickable.dataset.gtmPayload);
          payload = { ...payload, ...customPayload };
        } catch (e) {
          console.warn("[GTM] Failed to parse data-gtm-payload", e);
        }
      }

      // If explicit data-label exists, use it as the event name
      if (clickable.dataset?.label) {
        trackCustomEvent(toEventName(clickable.dataset.label), payload);
      } else {
        trackUserAction("click", payload);
      }
    };

    const handleChange = (event) => {
      const target = event.target;
      if (!target) return;
      if (!["input", "select", "textarea"].includes(target.tagName?.toLowerCase())) return;
      const label = getActionLabel(target);
      const payload = {
        label,
        tag: target.tagName?.toLowerCase(),
        type: target.type || undefined,
        name: target.name || undefined,
        id: target.id || undefined,
        path: location.pathname,
      };

      if (target.dataset?.label) {
        trackCustomEvent(toEventName(target.dataset.label), payload);
      } else {
        trackUserAction("change", payload);
      }
    };

    const handleSubmit = (event) => {
      const target = event.target;
      if (!target || target.tagName?.toLowerCase() !== "form") return;
      const label = target.getAttribute("name") || target.getAttribute("id") || "form_submit";
      trackUserAction("submit", {
        label,
        path: location.pathname,
      });
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("change", handleChange, true);
    document.addEventListener("submit", handleSubmit, true);
    applyMissingLabels();

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("change", handleChange, true);
      document.removeEventListener("submit", handleSubmit, true);
    };
  }, [location.pathname]);

  return null;
}

